import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight, Info, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { CONFIG } from '../mock/mock';
import { api } from '../lib/api';

export default function Verify() {
  const [traderId, setTraderId] = useState('');
  const [msg, setMsg] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d{4,15}$/.test(traderId.trim())) {
      setMsg({ type: 'error', text: 'Please enter a valid Trader ID (4-15 digits).' });
      return;
    }
    setLoading(true);
    const res = await api.submitTraderId(traderId.trim());
    setLoading(false);
    if (!res.ok) {
      setMsg({ type: 'error', text: res.message });
      return;
    }
    setStatus(res.status);
    setMsg({ type: res.status === 'approved' ? 'success' : 'info', text: res.message });
  };

  const goDashboard = async () => {
    const res = await api.login(traderId.trim());
    if (res.ok) navigate('/dashboard');
    else setMsg({ type: 'error', text: res.message });
  };

  return (
    <div className="alpha-bg min-h-screen">
      <Header />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center">
          <div className="text-[11px] tracking-[0.35em] text-cyan-300/80">SECURE VERIFICATION</div>
          <h1 className="font-display mt-2 text-4xl sm:text-5xl font-bold text-white">Verify Your <span className="text-cyan-gradient">Trader ID</span></h1>
          <p className="mt-4 text-slate-400">Submit your {CONFIG.brokerName} Trader ID for admin approval. Approval usually takes under 2 minutes.</p>
        </div>

        <div className="mt-10 glass-strong rounded-2xl p-6 sm:p-8 fade-up">
          <div className="flex items-center gap-3">
            <ShieldCheck className="h-5 w-5 text-cyan-300" />
            <div className="text-sm text-slate-300">Enter the numeric ID shown in your broker profile.</div>
          </div>

          <form onSubmit={submit} className="mt-6 grid gap-4">
            <label className="text-xs tracking-[0.25em] text-cyan-300/80">
              TRADER ID
              <input
                value={traderId}
                onChange={(e) => setTraderId(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="e.g. 12345678"
                className="field-dark mt-1 w-full rounded-lg px-4 py-3 text-base font-mono tracking-wider"
                maxLength={12}
              />
            </label>

            {msg && (
              <div className={`rounded-lg border px-3 py-2 text-sm flex items-start gap-2 ${
                msg.type === 'error' ? 'border-rose-400/40 bg-rose-500/10 text-rose-200'
                : msg.type === 'success' ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-200'
                : 'border-cyan-400/40 bg-cyan-500/10 text-cyan-200'
              }`}>
                {msg.type === 'success' ? <CheckCircle2 className="h-4 w-4 mt-0.5" /> : <Info className="h-4 w-4 mt-0.5" />}
                <span>{msg.text}</span>
              </div>
            )}

            <button disabled={loading} className="btn-cyan rounded-lg px-4 py-3 text-sm tracking-widest uppercase inline-flex items-center justify-center gap-2 disabled:opacity-70">
              {loading ? 'Verifying...' : (<>Submit Trader ID <ArrowRight className="h-4 w-4" /></>)}
            </button>

            {status === 'approved' && (
              <button type="button" onClick={goDashboard} className="btn-ghost rounded-lg px-4 py-3 text-sm tracking-widest uppercase">
                Enter Dashboard
              </button>
            )}
          </form>

          <div className="mt-6 grid gap-3 sm:grid-cols-3 text-xs">
            {['Approved usually < 2 min', 'Human + AI verification', 'Verified members only'].map((t, i) => (
              <div key={i} className="glass rounded-lg px-3 py-2 flex items-center gap-2 text-slate-300">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> {t}
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-slate-400">
          Don&apos;t have a {CONFIG.brokerName} account? <a href={CONFIG.referralLink} target="_blank" rel="noreferrer" className="text-cyan-300 hover:underline">Create one now</a>.
          <div className="mt-2">Already approved? <Link to="/login" className="text-cyan-300 hover:underline">Login here</Link>.</div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
